export const MCP_TOOL_UI_CATALOG = [
  {
    toolName: "generate_ideas",
    title: "Campaign Idea Generation",
    access: "read",
    summary:
      "Generate campaign directions from your draft and creator performance context.",
    creates:
      "A ranked set of campaign ideas with hooks and supporting rationale.",
    examplePrompt:
      "Here is my draft. Use generate_ideas to propose 5 campaign directions tailored to my audience.",
    featured: true,
  },
  {
    toolName: "fulfill_idea",
    title: "Idea to Offer Shortlist",
    access: "read",
    summary:
      "Expand one selected idea into ranked affiliate offer candidates and suggested copy (discovery only).",
    creates:
      "Offer candidates with linkStatus pending, fit rationale, and draft copy suggestions. Call materialize_offer_links for paste-ready URLs.",
    examplePrompt:
      "Take the strongest idea and use fulfill_idea to return 5 offer candidates. After I pick two, materialize tracked links for them.",
    featured: true,
  },
  {
    toolName: "search_prior_offers",
    title: "Collection Offer Search",
    access: "read",
    summary:
      "Search your prior/saved offers collection for relevant products by keyword.",
    creates:
      "A relevance-ranked list of offer candidates (linkStatus pending) from your collection.",
    examplePrompt:
      "Use search_prior_offers to find my best prior offers for back-to-school tech deals, then materialize links for the top two.",
    featured: true,
  },
  {
    toolName: "search_prior_broadcasts",
    title: "Prior Broadcast Search",
    access: "read",
    summary:
      "Find historical broadcasts by topic and date range to reuse winning angles.",
    creates:
      "Matched broadcasts with metadata/snippets you can reuse for current sends.",
    examplePrompt:
      "Use search_prior_broadcasts for spring-cleaning campaigns from last year and summarize what worked.",
    featured: true,
  },
  {
    toolName: "search_offers_for_drafts",
    title: "Draft Offer Matching",
    access: "read",
    summary:
      "Match unpublished draft content against your product vault for relevant offer candidates.",
    creates:
      "Draft-aligned offer candidates (linkStatus pending) with fit notes. Materialize links after you select offers.",
    examplePrompt:
      "Use search_offers_for_drafts for my current Kit draft and suggest which collection offers to feature.",
    featured: false,
  },
  {
    toolName: "get_creator_profile",
    title: "Creator Profile Context",
    access: "read",
    summary:
      "Retrieve audience, niche, and voice context before making recommendations.",
    creates:
      "A creator profile summary to ground strategy and messaging recommendations.",
    examplePrompt:
      "Use get_creator_profile first, then suggest 3 campaign angles aligned to my niche and audience.",
    featured: false,
  },
  {
    toolName: "get_top_performing_offers",
    title: "Top Performer Lookup",
    access: "read",
    summary:
      "Identify historically strongest offers in a selected time window.",
    creates:
      "A ranked list of top-performing offer candidates (linkStatus pending). Materialize links when ready to send.",
    examplePrompt:
      "Use get_top_performing_offers for the last 90 days and return the top 10 with why they won.",
    featured: true,
  },
  {
    toolName: "get_stale_winners",
    title: "Stale Winner Detection",
    access: "read",
    summary:
      "Find offers that performed strongly before but have not been used recently.",
    creates:
      "A revival list of underused winners (linkStatus pending) with historical performance context.",
    examplePrompt:
      "Use get_stale_winners to find strong offers we have not used in 4+ months, then materialize links for my top picks.",
    featured: true,
  },
  {
    toolName: "get_trending_offers",
    title: "Trending Opportunity Discovery",
    access: "read",
    summary:
      "Surface offers with rising momentum using seasonality and recent activity signals.",
    creates:
      "A trend-focused shortlist of offer candidates (linkStatus pending) with momentum context.",
    examplePrompt:
      "Use get_trending_offers with hint 'summer travel' and suggest the top opportunities for next send.",
    featured: true,
  },
  {
    toolName: "get_category_performance",
    title: "Category Performance Guidance",
    access: "read",
    summary:
      "Analyze category-level performance and drill into high-confidence category paths.",
    creates:
      "A category performance breakdown for placement and content-mix decisions.",
    examplePrompt:
      "Use get_category_performance to show my strongest categories this month and where I should focus.",
    featured: true,
  },
  {
    toolName: "get_price_band_performance",
    title: "Price Band Performance",
    access: "read",
    summary:
      "See which product price ranges historically drive the best CTR for this creator.",
    creates:
      "A price-band CTR breakdown ($0–$15 through $100+) with click share.",
    examplePrompt:
      "Use get_price_band_performance to show which price ranges convert best for my audience.",
    featured: true,
  },
  {
    toolName: "materialize_offer_links",
    title: "Materialize Offer Links",
    access: "read",
    summary:
      "Create shareable tracked affiliate URLs for offers selected from discovery tools.",
    creates:
      "Tracked nwsl.ink deep links (offer.url) ready for newsletter and outbound content.",
    examplePrompt:
      "Materialize tracked links for the two garden-tool offers I picked from fulfill_idea.",
    featured: true,
  },
  {
    toolName: "get_showcase_template",
    title: "Product Showcase HTML Template (deprecated)",
    access: "read",
    summary:
      "Deprecated: prefer build_showcase. Returns placeholder email-safe HTML for a product showcase box and inline affiliate links.",
    creates:
      "Wrapped showcase_html and inline_link_html with usage_instructions; replace documented placeholders only.",
    examplePrompt:
      "Prefer build_showcase with my materialized offers. Legacy: use get_showcase_template then fill placeholders.",
    featured: false,
  },
  {
    toolName: "build_showcase",
    title: "Build Product Showcase HTML",
    access: "read",
    summary:
      "Fill email-safe product showcase HTML from materialized offers (tracked affiliate URL + product image) using an optional ShowcaseSpec or the creator default.",
    creates:
      "showcases[] with fully populated showcase_html and inline_link_html — insert as-is.",
    examplePrompt:
      "After materialize_offer_links, call build_showcase with those offers and give me paste-ready showcase HTML.",
    featured: false,
  },
  {
    toolName: "use_showcase_spec",
    title: "Use Saved Showcase Spec",
    access: "read",
    summary:
      "Select a saved showcase recipe (or the creator default). Returns specId + library listing; pass specId into apply_monetize.",
    creates:
      "spec, specId, and library — pass specId into apply_monetize (preferred) or build_showcase.",
    examplePrompt:
      "Use my default showcase look, then pass specId into apply_monetize with my draft.",
    featured: false,
  },
  {
    toolName: "compile_showcase_spec",
    title: "Compile Showcase Spec",
    access: "write",
    summary:
      "Overlay style knobs onto a base recipe (optional specId/spec, else stock), save to the creator library (deduped), and return specId for apply_monetize.",
    creates:
      "spec plus specId and rationale — pass specId into apply_monetize (preferred insert path) or build_showcase.",
    examplePrompt:
      "Compile a lavender box background onto my current look, then pass specId into apply_monetize.",
    featured: false,
  },
  {
    toolName: "apply_monetize",
    title: "Apply Monetize Placements",
    access: "read",
    summary:
      "Splice one or more materialized offers into newsletter HTML (showcase boxes and text links). Omitting spec uses the creator default showcase recipe.",
    creates:
      "Complete monetized html plus placement receipts. Use html verbatim; does not publish.",
    examplePrompt:
      "After I pick offers and you materialize_offer_links, call apply_monetize with my draft HTML and those offers.",
    featured: true,
  },
];

export const MCP_TOOL_UI_CATALOG_BY_NAME = Object.fromEntries(
  MCP_TOOL_UI_CATALOG.map((tool) => [tool.toolName, tool])
);

export const MCP_DASHBOARD_TOOL_CARDS = MCP_TOOL_UI_CATALOG.filter(
  (tool) => tool.featured
);
